import React, {useCallback, useEffect, useState} from 'react';
import {CFComponentProps} from 'module/CustomField/type/CFComponentType';
import {CFValue} from 'module/CustomField/type/CFType';
import {useDqSearchRx} from 'module/Slct/impl/hook/useDqSearchRx'
import {log} from 'common/log'
import {delay, filter, take, tap} from 'rxjs/operators'
import {arrayToStringValue, getMultiInputParameters, stringValueToArray} from 'module/CustomField/util/MultiInputUtil'
import {useMemoParams} from 'module/Memo/hook/useMemoDq'
import {SlctMulti} from 'module/Slct/component/SlctMulti'
import {filterOptionsSlct} from 'module/Slct/util/filterOptionsSlct'
import {SlctMultiProps, SlctOnChangeState, SlctOption} from 'module/Slct/type/SlctType'
import {cssCFSlctLarge} from 'module/CustomField/style/cssCFInput'
import {useSlctCFAttrs} from 'module/Slct/hook/useSlctCFAttrs'
import {isDqSearchable} from 'module/Dq/util/DqParser'
import {getCFAttrTextAlign} from 'module/CustomField/style/CFAlignmentStyle'

export const CustomFieldSelectMultiComponent = React.memo(function CustomFieldSelectMultiComponent<ModelType extends object | void, ValueType extends string | number>(
	props: CFComponentProps<CFValue> & Pick<SlctMultiProps<ModelType, ValueType>, 'displayFormat'>,
) {
	const {
		cfDef,
		setCb,
		model,
		modelValue,
		disabled,
		placeholderHTML,
	} = props

	const {
		searchSubject,
		suggestions,
		byValueCache,
		loading,
		responseSubject,
	} = useDqSearchRx(props)

	const [disableManualSortPossible, setDisableManualSortPossible] = useState(false)
	let {separator, disableManualSort, maxAmount} = getMultiInputParameters(cfDef)
	const {menuWidth, displayFormat} = useSlctCFAttrs(cfDef)
	let values = stringValueToArray(modelValue, separator)
	const options = suggestions as SlctOption<ModelType, ValueType>[]
	const memoParams = useMemoParams(cfDef)

	const onChangeState = useCallback<SlctOnChangeState>(({focus, inputValue}) => {
		if (focus) {
			searchSubject.next({searchTerm: inputValue, model})
		} else {
			// map over all values in array? => use non-searchable dq for multi select!
			searchSubject.next({searchTerm: '', model})
		}
	}, [searchSubject, model])

	const onChange = useCallback((x: SlctOption<ModelType, ValueType>[]) => {
		const valueList = x.slice(0, maxAmount).map(x => x.value)
		const strValue = arrayToStringValue(valueList, separator, maxAmount)
		setCb(strValue)
		// focusNextFormElement() // menu should kept open on change
	}, [setCb])

	if (!disableManualSortPossible) {
		if (disableManualSort) log.warn('disableManualSort was not possible because dq.searchable = true', cfDef)
		disableManualSort = false
	}
	if (disableManualSort) {
		const sugVals = suggestions.map(x => x.value)
		values = [...values].sort((a, b) => sugVals.indexOf(a) - sugVals.indexOf(b))
	}

	useEffect(() => {
		// initializes missing labels by search yb value (only once)
		const sub = responseSubject.pipe(
			filter(x => !!x),
			take(1), // after first result
			delay(200),
			tap((res) => {
				if (res?.searchable) {
					setDisableManualSortPossible(false)
					const {byValueCache} = res
					values.forEach(value => {
						if (!byValueCache[value]) {
							searchSubject.next({searchTerm: value, model, onlyByValueCache: true})
						}
					})
				} else {
					setDisableManualSortPossible(true)
				}
			}),
		).subscribe()
		return () => sub.unsubscribe()
	}, [searchSubject, responseSubject, cfDef, model])

	return <div className={'CustomFieldSelectMultiComponent'} css={cssCFSlctLarge}>
		<SlctMulti
			disabled={disabled}
			placeholderHTML={placeholderHTML}
			options={options}
			byValueCache={byValueCache}
			loading={loading}
			values={values}
			onChange={onChange}
			onChangeState={onChangeState}
			filterOptions={isDqSearchable(cfDef.dqKey) ? undefined : filterOptionsSlct}
			memo={memoParams}
			disableManualSort={disableManualSort}
			menuWidth={menuWidth}
			displayFormat={displayFormat}
			align={getCFAttrTextAlign(cfDef)}
		/>
	</div>
})